import type { TOC } from '@ember/component/template-only';
import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { PrismIcon, PrismMessage } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { BREAKDOWNS } from 'lumen/utils/central-mapping-constants';

import StayRestrictionsAccordion, {
  type StayRestrictionsAccordionState,
} from './stay-restrictions-accordion';

interface StayRestrictionsPanelSignature {
  Args: {
    deepDiscountState: StayRestrictionsAccordionState;
    hotelState: StayRestrictionsAccordionState;
    showDeepDiscount: boolean;
    showDeepDiscountEmptyState: boolean;
  };
}

interface DeepDiscountSettingsLinkSignature {
  Args: {
    dataTestId?: string;
    label: string;
  };
}

/* Deep-links (new tab) into Central Mapping's Deep Discount breakdown. Shared by
   the accordion's "View in Settings" link and the empty-state CTA so the route,
   query param, styling and link-out affordance only live in one place. */
const DeepDiscountSettingsLink = <template>
  <LinkTo
    @query={{hash breakdown=BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP}}
    @route="hotel.settings.central-mapping.mapping"
    class="prism-link prism-link--small prism-link--standalone"
    data-test-id={{@dataTestId}}
    rel="noopener noreferrer"
    target="_blank"
  >
    <span class="flex items-center gap-50">
      {{@label}}
      <PrismIcon
        @color="inherit"
        @name="link-out"
        @size="small"
        aria-hidden="true"
      />
    </span>
  </LinkTo>
</template> satisfies TOC<DeepDiscountSettingsLinkSignature>;

const StayRestrictionsPanel = <template>
  <StayRestrictionsAccordion
    @id="hotel-min-los"
    @state={{@hotelState}}
    @title={{t "general.hotel"}}
  />
  {{#if @showDeepDiscount}}
    <StayRestrictionsAccordion
      @id="deep-discount-min-los"
      @showEmptyState={{@showDeepDiscountEmptyState}}
      @state={{@deepDiscountState}}
      @title={{t "pricing_assistant.stay_restrictions.deep_discount"}}
    >
      <:settingsLink>
        <DeepDiscountSettingsLink
          @label={{t
            "pricing_assistant.stay_restrictions.view_deep_discount_rates_in_settings"
          }}
        />
      </:settingsLink>
      <:emptyState>
        <div
          class="flex w-full justify-center py-500"
          data-test-id="deep-discount-empty-state"
        >
          <PrismMessage
            @description={{t
              "pricing_assistant.stay_restrictions.empty_state.description"
            }}
            @illustration="100x100_empty"
          >
            <:link>
              <DeepDiscountSettingsLink
                @dataTestId="deep-discount-empty-state-settings-link"
                @label={{t "general.go_to_settings"}}
              />
            </:link>
          </PrismMessage>
        </div>
      </:emptyState>
    </StayRestrictionsAccordion>
  {{/if}}
</template> satisfies TOC<StayRestrictionsPanelSignature>;

export default StayRestrictionsPanel;
